/**
 * The four particle effects: chimney smoke, the pirtti window coming apart,
 * blood off a knuckle, and what a vegetarian breakfast does on the way back up.
 *
 * Render-only, but two of them are timed off a simulation penalty they must
 * not outlive - `GLASS_PAIN.duration` and `AAMUPALA.vomitDuration`. See
 * `src/config/AGENTS.md`.
 */

/**
 * The shape of a climb, shared by every plume of smoke in the level.
 *
 * Where a plume starts, how far it rises and how wide it gets are authored per
 * chimney with the rest of the level dressing (`SmokeSpec` in world/Level.ts).
 * This is the *curve* those numbers are walked along, and it is deliberately
 * one curve for all of them: smoke behaves the same whatever it is coming out
 * of, and a second set of these would only ever be a way of getting it wrong
 * twice.
 *
 * Rendering only. Nothing here is stepped by the simulation or hashed into the
 * fingerprint, so it can be tuned on looks alone - see src/render/Plume.ts.
 */
export const SMOKE = {
  /** Climb, as a power of age. Under 1: out of the pipe fast, then slowing. */
  climb: 0.72,
  /** Spread, the same way, and harder: most of the widening happens early. */
  spread: 0.55,
  /**
   * Drift, as a power of age - and over 1 on purpose. A puff catches more of
   * the wind as it loses the draught that threw it, which is what bends a
   * column over at the top instead of shearing it sideways at the mouth.
   */
  lean: 2,
  /** How far off its line a puff wanders by the end of the climb, metres. */
  wander: 0.45,
  /** Wanders per climb. Slow: this is buoyancy, not wind. */
  wanderRate: 3.1,
  /**
   * Fraction of its life a puff spends coming up to full opacity. Short on
   * purpose: anything slower leaves a gap of clear air between the pipe and
   * the first smoke you can see, and smoke that starts a metre up reads as
   * something floating over the yard rather than as something coming out.
   */
  fadeIn: 0.04,
  /** Thinning, as a power of the life it has left. */
  fadeOut: 1.5,
  /**
   * How much of the spacing between puffs is allowed to be uneven. Dead-even
   * spacing reads as a string of beads coming out of the pipe.
   */
  jitter: 0.7,
  /** How fast a puff turns over its climb, radians, before the sign. */
  spin: 1.2,
} as const;

/**
 * The pane in Einon pirtti's window, coming apart.
 *
 * Rendering only. Nothing here is stepped by the simulation or hashed into the
 * fingerprint - the simulation's entire opinion about the glass is *when* it
 * broke and *where* it was hit (`src/world/Ikkuna.ts`), and this is only what
 * that looks like. The pane is not a collider before the break or after it, so
 * every one of these can be tuned on looks alone.
 *
 * The whole thing is a pure function of how long ago it broke, the same way a
 * plume is a pure function of the clock: no emitter, no pool, nothing that
 * accumulates. Ask where shard `i` is 0.4 s in and you get the same answer on
 * every machine, however many frames it took to get there - and a run that is
 * restarted three times in ten seconds leaks nothing.
 *
 * See src/render/Shatter.ts.
 */
export const SHATTER = {
  /**
   * How the pane is cut up, across and up.
   *
   * The same both ways, because the pirtti's pane is square and a grid that is
   * not gives shards that are noticeably taller than they are wide - which at
   * this size reads as a window full of falling paper rather than glass. If
   * the window is ever reshaped, these two move with it.
   *
   * 14 x 14 is 196 pieces: far more than the eye can count going past at
   * 30 km/h, and few enough that the whole break is 196 small transparent
   * quads that exist for about a second and a half, once, per run.
   */
  columns: 14,
  rows: 14,
  /**
   * How much of its own cell a shard fills.
   *
   * Exactly 1, and it has to be. The frame the window breaks on is the frame
   * the sheet is swapped for the pieces, so on that frame the pieces *are* the
   * sheet - and anything under 1 leaves a gap of unglazed air between every
   * pair of them. Tried at 0.86 on the theory that a hair of gap would stop
   * the grid reading as a grid: it does the exact opposite. Ninety-six panes
   * of tinted glass with clear lines between them is a window with bars on it,
   * and it pops into existence a tenth of a second before anything moves.
   *
   * What keeps it from reading as graph paper is that it only has to hold for
   * one frame. By the next one `impactPush` and `delay` have taken the middle
   * out of it, and a grid with a hole blown through the middle is not a grid.
   */
  fill: 1,
  /** Metres per second, straight out through the hole the runner made. */
  push: 2.6,
  /**
   * How much harder the glass nearest the impact leaves than the corners. The
   * pane does not come apart evenly - it goes where it was hit and the rest
   * follows, which is what this and `delay` between them buy.
   */
  impactPush: 4.4,
  /** How far from the impact that extra push has died away to nothing, 0..1. */
  impactReach: 0.55,
  /** Seconds the far corners hang on for after the middle has gone. */
  delay: 0.11,
  /** Sideways and upward scatter, metres per second, before the seed's sign. */
  spread: 1.5,
  lift: 1.1,
  /**
   * Gravity for a piece of glass. The real number, because a shard is dense
   * enough that air does nothing to it over the metre and a half it falls.
   */
  gravity: 9.81,
  /** Tumble, radians per second, before the seed's sign. */
  spin: 9.0,
  /**
   * Seconds a shard spends fading out once it is down. Short: this is glass in
   * long grass, and it is behind you by the time it lands.
   */
  settle: 0.45,
} as const;

/**
 * ==========================================================================
 *  THE BLOOD ON THE SCREEN, AFTER THE PANE
 * ==========================================================================
 *  Going out through a closed window costs three seconds at three quarters
 *  speed, and until now the only thing on the screen that said so was the
 *  yell. A penalty you cannot see is indistinguishable from the game being
 *  inconsistent - the same argument the speedometer and the beer bar are here
 *  for - so `GLASS_PAIN` gets a picture: a handful of marks thrown across the
 *  view on the frame the glass goes, running and fading, and gone on the frame
 *  the legs come back.
 *
 *  That last part is why there is no duration in this block. The blood lasts
 *  exactly `GLASS_PAIN.duration`, because what it is *for* is showing how long
 *  is left of it - a splatter that outlived the slow, or died before it, would
 *  be worse than no splatter at all.
 *
 *  ------------------------------------------------------------------------
 *   SMALL ON PURPOSE
 *  ------------------------------------------------------------------------
 *  Nothing lands within `keepOut` of the crosshair, most of it is out at the
 *  edges, and the biggest mark is `radius.max` of the screen's height across
 *  the radius - about forty pixels on a 1080p display. This is still a race
 *  and it is still being run forwards: the three seconds are the punishment,
 *  and the blood is only the receipt. Anything you have to see *past* would be
 *  charging for the window twice.
 *
 *  On the screen rather than in the world, and painted over the frame rather
 *  than into it. This is blood on the eye: it does not warp with the acid
 *  layer, it does not have to be looked at to be seen, and it costs nothing at
 *  all on the ninety-odd per cent of frames it is not up for.
 *
 *  Rendering only, and outside the fingerprint - see src/config/CLAUDE.md. The
 *  marks cannot decide where anybody ends up. `GLASS_PAIN`, which can, is the
 *  hashed half of the pair.
 *
 *  Positions are fractions of the screen: `x` of its width, `y` and every
 *  radius of its height. See src/render/Blood.ts for the arithmetic and
 *  src/ui/BloodOverlay.ts for the canvas it is painted on.
 * ==========================================================================
 */
export const BLOOD = {
  /**
   * How many marks. Enough to read as a spray rather than as three dots
   * somebody placed, few enough that the view between them is still a view.
   */
  count: 14,
  /**
   * How far from the middle of the screen the nearest mark may land, as a
   * fraction of the box. Measured in the box's own units, so a wide screen
   * keeps a wider corridor clear - which is the right way round: what is being
   * protected is the crosshair and the doorway behind it, and on a wide screen
   * both are further from the edges.
   */
  keepOut: 0.19,
  /**
   * How far out the furthest may land. Just over 0.5, so the outermost marks
   * hang off the edge of the frame - blood that stops politely inside it is
   * blood somebody placed - but never far enough for a whole mark to land on a
   * screen that is not there.
   */
  reach: 0.52,
  /**
   * Placement bias, as a power of the roll. Under 1 pushes marks towards
   * `reach`: blood that arrives evenly across the view reads as a pattern, and
   * blood that arrives at the edges reads as blood.
   */
  outward: 0.55,
  /** Radius of a mark, as a fraction of the screen's height. */
  radius: { min: 0.008, max: 0.038 },
  /**
   * Size bias, as a power of the roll. Over 1 keeps most of them small: a
   * spray is a lot of specks and two or three drops, not fourteen drops.
   */
  sizeBias: 1.9,
  /**
   * How solid a mark is at its fullest. Not 1 - there is a yard behind it, and
   * blood you cannot see the yard through is paint. Not far off it either: a
   * thinner mark over grass comes out brown, and brown is mud.
   */
  alpha: { min: 0.68, max: 0.94 },
  /** Seconds a mark takes to arrive. Short: it was thrown, not poured. */
  land: 0.05,
  /**
   * Seconds between the first mark landing and the last. Short: the whole
   * point of the splatter is the *moment* the glass went, and a spray that
   * assembles itself over a quarter of a second is a spray that lands after
   * the thing it is reporting. Not zero either - fourteen marks appearing on
   * one frame is a texture being switched on.
   */
  stagger: 0.12,
  /**
   * Fraction of the three seconds spent at full before the fade starts. The
   * fade is the timer: by the time you can see through it again, you have your
   * legs back.
   */
  hold: 0.45,
  /**
   * How far the heaviest mark runs down the screen over the whole three
   * seconds, as a fraction of the height. Scaled by size, so the drops run and
   * the specks stay where they landed - which is what tells the eye that the
   * big ones are wet.
   */
  run: 0.062,
  /**
   * The run, as a power of age. One, i.e. straight: a real drop hangs and then
   * goes, and a real drop is not being looked at for three seconds either. Any
   * power over one puts the whole run into the tail of the fade, where it is a
   * thing that happened to blood nobody can see any more.
   */
  runPower: 1,
  /** Width of the trail it leaves behind it, as a fraction of its radius. */
  dripWidth: 0.34,
  /**
   * How far a mark's outline wanders off round, 0..1, over `lobes` humps. A
   * circle of blood is a bullet hole; the wobble is the difference between a
   * mark that was thrown at the screen and one that was drawn on it.
   */
  wobble: 0.34,
  lobes: 3,
  /**
   * How much longer a mark is along the way it was going than across it, and
   * how far the spike at its trailing end reaches, in radii.
   *
   * This is most of what separates blood from a red dot. A drop that arrives
   * square-on is a circle, and nothing here arrives square-on: it came out of
   * a window sideways, with the runner. Every mark is thrown outwards from the
   * middle of the view for the same reason - the whole spray is one thing that
   * happened, coming at you, and marks pointing in fourteen directions read as
   * fourteen separate accidents.
   */
  stretch: 0.55,
  tail: 1.0,
  /** How far off straight outward a mark may be thrown, radians either way. */
  headingSpread: 0.9,
  /**
   * How many specks a mark may throw off, how big and how far out - and how
   * wide a fan they leave in, radians either side of the way it was going.
   * Forwards, because that is where a drop that broke up is going: specks that
   * ring a mark evenly are a mark with a halo.
   */
  satellites: 3,
  satelliteRadius: { min: 0.12, max: 0.32 },
  satelliteReach: 3.4,
  satelliteSpread: 1.5,
  /**
   * The colour, across the middle and at the rim. Dark, and nearly flat: the
   * gradient is a hair of depth and a darker edge, not a highlight. A drop
   * with a hot spot in it is a bead of something, and the surest way to make
   * fourteen marks read as jam is to light them.
   */
  core: 0xa8141a,
  edge: 0x4a0509,
  /**
   * Where the flat middle of a mark ends and the darker rim begins, as a
   * fraction of its radius. Everything past the radius - the point it was
   * drawn out into, the specks, the trail - is rim colour, which is the right
   * way round: thin blood is dark blood.
   *
   * A gradient rather than a line round the outline. An even dark line at an
   * even width is a cartoon; this way the dark falls where the mark is thin
   * and the wobble decides where that is.
   */
  rimFrom: 0.72,
  /**
   * How soft the edges are, as a fraction of the screen's height. This is on
   * glass a few centimetres from an eye that is focused on a yard, and a mark
   * with a pixel-sharp edge is a decal. Two pixels at 1080p is most of the
   * difference between blood on the screen and blood drawn on the screen.
   */
  softness: 0.0018,
} as const;

/**
 * ==========================================================================
 *  OKSENNUS - what a mouthful of parsakaalia looks like from behind Kimmo's
 *  eyes
 * ==========================================================================
 *  Render-only, exactly like `BLOOD` above, and for exactly the same reason:
 *  the cost of eating a vegetable is `AAMUPALA.vomitDuration`, which is hashed
 *  and lives with the rest of the minigame's tuning. This is only the picture
 *  of that cost, so none of it is in `simulationFingerprint()` and all of it
 *  can be re-tuned for free. See src/config/CLAUDE.md.
 *
 *  It has no duration of its own for the same reason the blood has none: a
 *  splatter that outlived the seconds it was reporting on would be a picture
 *  of a penalty that had already been paid.
 *
 *  The shape is different from the blood's, though, and deliberately. Blood
 *  arrives *at* the screen from outside and sprays outwards from the middle;
 *  this comes from below, because it is Kimmo's own and his mouth is under the
 *  bottom edge of the view. So the gouts are thrown up out of the bottom of
 *  the frame, they run down it fast and heavy, and the whole view goes green
 *  while it happens.
 * ==========================================================================
 */
export const OKSENNUS = {
  /** How many gouts. Fewer and bigger than the blood: this is not a spray. */
  count: 9,
  /**
   * Where they come from, as fractions of the frame: a mouth's width either
   * side of the middle, at and just under the bottom edge. Off the bottom on
   * purpose - a gout whose whole outline is inside the frame is a blob
   * somebody drew, and this one has a man attached to the underneath of it.
   */
  mouth: { x: 0.5, y: 1.02, spread: 0.13 },
  /**
   * How high up the frame the furthest one is thrown, as a fraction of the
   * height, and how far off vertical it may go. Not far: it is a heave, not a
   * fountain, and the top half of the screen is the half a runner needs.
   */
  reach: 0.42,
  sideways: 0.3,
  /** Radius of a gout, as a fraction of the screen's height. */
  radius: { min: 0.03, max: 0.105 },
  /** Size bias, as a power of the roll. Under 1, so most of them are big. */
  sizeBias: 0.8,
  /** How solid at its fullest. Thicker than blood, because it is thicker. */
  alpha: { min: 0.72, max: 0.95 },
  /**
   * Seconds the throw takes, and seconds between the first one leaving and the
   * last. Longer than the blood's stagger by a lot: blood is one moment and
   * this is three or four heaves in a row.
   */
  rise: 0.22,
  stagger: 0.5,
  /** Fraction of the life spent at full before it starts to clear. */
  hold: 0.5,
  /**
   * How far the heaviest gout runs back down the screen once it has stopped
   * climbing, as a fraction of the height, and the power the run takes. Over
   * one, unlike the blood: this hangs for a moment and then goes, which is
   * what something this heavy does on glass.
   */
  run: 0.5,
  runPower: 1.7,
  /** Width of the trail behind it, as a fraction of its radius. */
  dripWidth: 0.5,
  /** How far a gout's outline wanders off round, over this many humps. */
  wobble: 0.4,
  lobes: 3,
  /**
   * How much longer a gout is along the way it was going than across it, and
   * the spike off its trailing end in radii. Lower than the blood's both ways:
   * this is coming *out* rather than arriving, so it is a lump with a tail
   * rather than a drop that hit something at speed.
   */
  stretch: 0.35,
  tail: 0.7,
  /** Specks: how many, how big and how far out. */
  satellites: 4,
  satelliteRadius: { min: 0.1, max: 0.26 },
  satelliteReach: 3.0,
  satelliteSpread: 1.8,
  /**
   * The colour, across the middle and at the rim. Parsakaali and falafel and
   * whatever was in the bottle: khaki rather than green, because a green that
   * reads as green reads as slime, and the joke is only funny if it reads as
   * breakfast.
   */
  core: 0x9aa03c,
  edge: 0x4f4416,
  rimFrom: 0.7,
  softness: 0.0026,
  /**
   * The wash over the whole frame while it is happening: colour, and how far
   * up its alpha goes at the peak. This is the half of the effect that says it
   * is *Kimmo* being sick rather than something happening to the window, and
   * it is the half that has to stay well under a curtain - the run is still
   * going, and a screen you cannot see the porstua through is a screen that
   * has taken the run away rather than charged for it.
   */
  wash: 0x6f7a2a,
  washAlpha: 0.3,
} as const;
